import { formatDistanceToNow } from 'date-fns';
import { useRouter } from 'expo-router';
import { useCallback, useMemo } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { FadeIn, sectionEntrance } from '../../src/components/animations';
import { ShimmerSkeleton, SkeletonCard } from '../../src/components/animations/Skeleton';
import { Box } from '../../src/components/primitives/Box';
import { VStack } from '../../src/components/primitives/Stack';
import { TAB_ROUTE_BY_HREF } from '../../src/config/navigation';
import { useAuth } from '../../src/contexts/AuthContext';
import { useEdgeToEdge } from '../../src/contexts/LayoutChromeContext';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { useDashboardSummary } from '../../src/features/analytics/hooks/useAnalyticsData';
import { useCampaignActiveCount } from '../../src/features/campaigns/hooks/useCampaigns';
import {
  type Activity,
  DashboardCharts,
  DashboardChartsLoading,
  DashboardHeader,
  type MetricData,
  QuickActions,
  RecentActivity,
  StatsOverview,
} from '../../src/features/dashboard';
import { OnboardingChecklist } from '../../src/features/onboarding';
import { useDismissOnboarding, useOnboardingDismissed } from '../../src/features/onboarding/hooks';
import { useLeadStats } from '../../src/hooks/useLeads';
import { useTabNavResetKey } from '../../src/hooks/useTabNavResetKey';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';

function getTimeOfDayGreeting(t: (key: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 12) return t('dashboard.greeting.morning');
  if (hour < 18) return t('dashboard.greeting.afternoon');
  return t('dashboard.greeting.evening');
}

function getFirstName(
  backendUser: { fullName: string } | null,
  auth0User: { name?: string } | null,
): string {
  if (backendUser?.fullName) {
    return backendUser.fullName.split(' ')[0];
  }
  if (auth0User?.name) {
    return auth0User.name.split(' ')[0];
  }
  return 'there';
}

function _DashboardLoadingSkeleton() {
  return (
    <Box flex={1} backgroundColor={colors.background.primary} padding="xl">
      <VStack spacing="xxl">
        <VStack spacing="md">
          <ShimmerSkeleton width={180} height={14} borderRadius={8} />
          <ShimmerSkeleton width={280} height={28} borderRadius={8} />
          <ShimmerSkeleton width={320} height={16} borderRadius={8} />
        </VStack>

        <Box flexDirection="row" gap="md" flexWrap="wrap">
          {['s1', 's2', 's3', 's4'].map((skeletonId) => (
            <Box key={skeletonId} style={{ flex: 1, minWidth: 200 }}>
              <SkeletonCard showIcon={false} showDescription={false} />
            </Box>
          ))}
        </Box>

        <Box flexDirection="row" gap="lg">
          <Box style={{ flex: 1 }}>
            <SkeletonCard />
          </Box>
          <Box style={{ flex: 1 }}>
            <SkeletonCard />
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}

function useActivityTypeLabels(): Record<string, string> {
  const { t } = useTranslation();
  return {
    created: t('dashboard.activityTypes.leadCreated'),
    lead_created: t('dashboard.activityTypes.leadCreated'),
    stage_change: t('dashboard.activityTypes.stageChanged'),
    stage_changed: t('dashboard.activityTypes.stageChanged'),
    note_added: t('dashboard.activityTypes.noteAdded'),
    assigned: t('dashboard.activityTypes.leadAssigned'),
    email_sent: t('dashboard.activityTypes.emailSent'),
  };
}

function mapActivityType(backendType: string): Activity['type'] {
  const typeMap: Record<string, Activity['type']> = {
    created: 'created',
    lead_created: 'lead_created',
    stage_change: 'stage_change',
    stage_changed: 'stage_changed',
    note_added: 'note_added',
    assigned: 'assigned',
    email_sent: 'email_sent',
  };
  return typeMap[backendType] ?? 'created';
}

export default function DashboardScreen() {
  const router = useRouter();
  // Take the full available space (no DashboardLayout content padding), the
  // same way chat and voice-agents are built — the screen manages its own
  // padding via scrollContent below.
  useEdgeToEdge();
  const { backendUser, auth0User, currentOrganization } = useAuth();
  const dismissOnboarding = useDismissOnboarding();
  const { t } = useTranslation();
  const activityTypeLabels = useActivityTypeLabels();
  const { data: stats, isLoading: isStatsLoading } = useLeadStats(currentOrganization?.id);
  const { data: activeCampaignData, isLoading: isCampaignLoading } = useCampaignActiveCount(
    currentOrganization?.id,
  );
  const { data: dashboardSummary, isLoading: isSummaryLoading } = useDashboardSummary(
    currentOrganization?.id,
    '30d',
  );
  const { width } = useWindowDimensions();
  const { dismissed: onboardingDismissed, markDismissed } = useOnboardingDismissed(
    currentOrganization?.id,
  );

  const handleDismissOnboarding = useCallback(() => {
    // Hides it here immediately and records it on this device.
    markDismissed();

    // And on the server, which is what emits onboarding.dismissed and what keeps
    // it hidden on the user's other devices. Never awaited or surfaced: the user
    // has already dismissed it, and a failed call must not undo that.
    if (currentOrganization?.id) {
      dismissOnboarding.mutate({ organizationId: currentOrganization.id });
    }
  }, [currentOrganization?.id, dismissOnboarding.mutate, markDismissed]);

  const firstName = useMemo(() => getFirstName(backendUser, auth0User), [backendUser, auth0User]);

  const greeting = useMemo(() => getTimeOfDayGreeting(t), [t]);

  // Remount the scroll view on each chrome navigation so returning to Dashboard
  // lands at the top (FLOSEAI-416).
  const scrollResetKey = useTabNavResetKey(TAB_ROUTE_BY_HREF['/dashboard']);

  const metrics: MetricData[] = useMemo(() => {
    const totalLeads = stats?.totalLeads ?? 0;
    const stages = stats?.stagesBreakdown ?? [];
    const wonStage = stages.find(
      (s) => s.name.toLowerCase().includes('won') || s.name.toLowerCase().includes('closed'),
    );
    const conversionRate =
      totalLeads > 0 && wonStage ? Math.round((wonStage.count / totalLeads) * 100) : 0;

    return [
      {
        id: 'total-leads',
        title: t('dashboard.metrics.visitors.title'),
        value: totalLeads.toLocaleString(),
        subtitle: t('dashboard.metrics.visitors.subtitle'),
        metricType: 'visitors' as const,
      },
      {
        id: 'revenue',
        title: t('dashboard.metrics.revenue.title'),
        value: '$0',
        subtitle: t('dashboard.metrics.comingSoon'),
        metricType: 'revenue' as const,
      },
      {
        id: 'conversion-rate',
        title: t('dashboard.metrics.conversion.title'),
        value: `${conversionRate}%`,
        subtitle: t('dashboard.metrics.conversion.subtitle'),
        metricType: 'conversion' as const,
      },
      {
        id: 'campaigns',
        title: t('dashboard.metrics.campaigns.title'),
        value: isCampaignLoading ? '—' : (activeCampaignData?.count ?? 0),
        subtitle: t('dashboard.metrics.campaigns.subtitle'),
        metricType: 'campaigns' as const,
      },
    ];
  }, [stats, activeCampaignData, isCampaignLoading, t]);

  const activities: Activity[] = useMemo(() => {
    if (!stats?.recentActivities) return [];

    return stats.recentActivities.map((activity) => {
      const leadName =
        (activity.lead as { company?: string } | undefined)?.company ??
        activity.lead?.fullName ??
        activity.lead?.email ??
        t('dashboard.activityTypes.unknownLead');
      return {
        id: activity.id,
        type: mapActivityType(activity.activityType),
        title: activityTypeLabels[activity.activityType] ?? t('dashboard.activityTypes.activity'),
        description: activity.description ?? `${leadName} - ${activity.activityType}`,
        timestamp: formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true }),
      };
    });
  }, [stats?.recentActivities, activityTypeLabels, t]);

  const isDesktop = width >= tokens.breakpoints.desktop;
  const isTablet = width >= tokens.breakpoints.tablet;

  const personalizedTitle = `${greeting}, ${firstName}`;

  return (
    <ScrollView
      key={`dashboard-${scrollResetKey}`}
      testID="dashboard_screen"
      style={screenStyles.root}
      contentContainerStyle={screenStyles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Greeting, then stats, then charts, then activity — one step apart. */}
      <VStack spacing="xxl">
        <FadeIn {...sectionEntrance(0)}>
          <DashboardHeader
            badge={t('dashboard.header.badge')}
            title={personalizedTitle}
            titleTestID="dashboard_greeting_text"
            subtitle={t('dashboard.header.subtitle')}
          />
        </FadeIn>

        {!onboardingDismissed && !isStatsLoading && (
          <FadeIn {...sectionEntrance(1)}>
            <OnboardingChecklist
              totalLeads={stats?.totalLeads ?? 0}
              hasStageChange={(stats?.recentActivities ?? []).some(
                (a) => a.activityType === 'stage_change' || a.activityType === 'stage_changed',
              )}
              teamMemberCount={backendUser?.organizations?.[0] ? 1 : 0}
              onDismiss={handleDismissOnboarding}
            />
          </FadeIn>
        )}

        <FadeIn {...sectionEntrance(2)}>
          <StatsOverview metrics={metrics} loading={isStatsLoading || isCampaignLoading} />
        </FadeIn>

        <FadeIn {...sectionEntrance(3)}>
          {isSummaryLoading && <DashboardChartsLoading />}
          {dashboardSummary && <DashboardCharts summary={dashboardSummary} />}
        </FadeIn>

        <FadeIn {...sectionEntrance(4)}>
          <Box flexDirection={isDesktop ? 'row' : 'column'} alignItems="stretch" gap="lg">
            <Box style={isDesktop ? { flex: 1 } : undefined}>
              <RecentActivity
                activities={activities}
                onViewAll={() => router.push('/analytics')}
                loading={isStatsLoading}
              />
            </Box>
            {isTablet && (
              <Box style={isDesktop ? { flex: 1 } : undefined}>
                <QuickActions
                  onAddLead={() => router.push('/lead/new')}
                  onViewAnalytics={() => router.push('/analytics')}
                  onRunWorkflow={() => router.push('/workflows')}
                  onVoiceCampaign={() => router.push('/voice-agents')}
                />
              </Box>
            )}
          </Box>
        </FadeIn>
      </VStack>
    </ScrollView>
  );
}

const screenStyles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
  },
});
